import type { SessionData } from '@@/shared-components/types/Internal'
import type { Auth, LoginChallenge } from '@@/shared-components/types/Auth'
import type { RouteLocationResolvedGeneric, RouteRecordNormalized } from 'vue-router'
// Auth plugin
// Login a Logout metody
// Držení Laravel Sanctum tokenu v cookie
// Podporuje dvě metody přihlášení:
//   - token (výchozí): Bearer token uložený v cookie auth-token
//   - SPA: Laravel Sanctum session (httponly cookie + XSRF-TOKEN)

const tokenCookieKey = 'auth-token'
const foreignUserKey = 'foreign-user'
const expiresAtKey = 'expires-at'
const backLinkKey = 'back-link'

type LoginConfig = {
  headers?: Record<string, string>
  type?: string
  loginUrl?: string | ((baseUrl: string) => string)
  body?:
    | Record<string, unknown>
    | ((originalBody: Record<string, unknown>) => Record<string, unknown>)
}

export default defineNuxtPlugin({
  parallel: true,
  dependsOn: ['i18n:plugin', 'shared-components-plugin'],
  setup: async (nuxtApp) => {
    const { public: config } = useRuntimeConfig()

    // SPA (Sanctum httponly cookie) vs token (Bearer) režim. Zapíná se přes
    // `sharedComponents.auth.sanctumSpa` (modul nastaví runtimeConfig.public.sanctumSpa).
    const sanctumSpa =
      (config as Record<string, unknown>).sanctumSpa === true ||
      nuxtApp.$sharedComponentsOptions?.options?.auth?.sanctumSpa === true

    // V SPA režimu není v JS žádný token – přihlášení poznáme podle načteného uživatele.
    const isLoggedIn = () => (sanctumSpa ? auth.value.user !== null : auth.value.token !== null)

    const apiBaseUrl = config.apiBaseUrl ? config.apiBaseUrl : '$NUXT_PUBLIC_API_BASE_URL'
    const { browser, os } = useGetBrowser()
    const {
      $sentry,
      $i18n: { t },
    } = nuxtApp

    const isForeignUserCookie = useCookie(foreignUserKey, { sameSite: true, secure: true })
    const expiresAtCookie = useCookie(expiresAtKey, { sameSite: true, secure: true })

    const { addSuccessMessage, addErrorMessage } = useToastService()

    const throwMissingPermissionsError = async (permissionKey: string) => {
      const data = await checkSession()
      // Kontrola, zda oprávnění skutečně chybí
      if (data?.abilities?.find((p: string) => p === permissionKey)) {
        return
      }
      const toast = usePrimeVueToast()
      const addErrorMessage = (message: string, heading: string = 'Chyba', life = 600000) => {
        toast.add({
          severity: 'error',
          summary: heading,
          detail: message,
          life,
        })
      }

      addErrorMessage(
        `${t('Pokud si myslíte že se jedná o chybu, kontaktuje správce webu')}.`,
        `${t('Nedostatečná oprávnění')} - ${permissionKey}`
      )
      const error = new Error(t('Nedostatečná oprávnění'))
      error.code = 403
      return abortNavigation({ ...error, fatal: true })
    }
    // Vstupní stránka po přihlášení. Kandidáty označuje projekt v definePageMeta
    // flagem `homePage` (respektive `afterLoginPage`, který má přednost). Kandidátů
    // může být víc a vybere se první, do kterého má uživatel oprávnění podle
    // `meta.permissions` – uživatel bez práv do hlavní části aplikace tak skončí na
    // své vlastní vstupní stránce. Pořadí určuje číselná hodnota flagu
    // (`true` = 0, vyšší číslo = nižší priorita); na pořadí z `router.getRoutes()`
    // spoléhat nelze, to je seřazené podle skóre matcheru vue-routeru (statická
    // podstránka je tam dřív než kořenová `/`).
    const getEntryPageRequiredPermissions = (route: RouteRecordNormalized) => {
      const { permissions: required } = route.meta as {
        permissions?: string[] | ((route: RouteLocationResolvedGeneric) => string[])
      }
      if (typeof required !== 'function') {
        return required ?? []
      }
      try {
        return required(useRouter().resolve(route.path))
      } catch {
        return []
      }
    }

    const getEntryPagePriority = (route: RouteRecordNormalized) => {
      const flag = route.meta.afterLoginPage ?? route.meta.homePage
      return (route.meta.afterLoginPage ? 0 : 1000) + (typeof flag === 'number' ? flag : 0)
    }

    const getEntryPage = () => {
      return useRouter()
        .getRoutes()
        .filter((r) => (r.meta.afterLoginPage || r.meta.homePage) && !r.meta.disabled)
        .sort((a, b) => getEntryPagePriority(a) - getEntryPagePriority(b))
        .find((r) => getEntryPageRequiredPermissions(r).every((p) => permissions.value[p]))?.path
    }

    let sessionVerified = false

    const checkSession = async () => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      if (auth.value.token) {
        headers['Authorization'] = `Bearer ${auth.value.token}`
      } else if (sanctumSpa && import.meta.server) {
        // In SPA mode on SSR, forward the browser's cookies so Sanctum can verify the session
        const reqHeaders = useRequestHeaders(['cookie'])
        if (reqHeaders.cookie) headers['Cookie'] = reqHeaders.cookie
      }

      try {
        const session = await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions`, {
          headers,
          // SPA: httponly cookie se odešle jen s credentials: 'include'
          credentials: sanctumSpa ? 'include' : undefined,
        })
        if (session) {
          auth.value.user = session.data.identity ?? null
          auth.value.permissions = session.data.abilities
        }
        return session.data
      } catch {
        // Nepřihlášený uživatel (401) – nevyhazujeme, vrátíme null a necháme
        // middleware přesměrovat na login.
        return null
      }
    }

    addRouteMiddleware(
      'auth',
      async (to) => {
        const router = useRouter()
        const loginPage = router.getRoutes().find((r) => r.meta.loginPage)?.path

        if (!to.meta.public && to.meta.middleware !== 'link') {
          if (!sessionVerified) {
            if (auth.value.user !== null && auth.value.permissions !== null) {
              sessionVerified = true
            } else {
              if (!sanctumSpa && auth.value.token === null) {
                getToken()
              }
              if (auth.value.user === null) {
                await checkSession()
              }

              if (!isLoggedIn() || !auth.value.permissions) {
                if (import.meta.client) {
                  localStorage.setItem(
                    backLinkKey,
                    window.location.pathname + window.location.search
                  )
                }
                return loginPage
              }
            }
          }
        }
        if (isLoggedIn() && to.path === loginPage) {
          return getEntryPage()
        }

        // Souhlas s obchodními podmínkami funguje jako guard přihlášení a aplikuje
        // se jen když je povolen plugin `plugin.terms`. Dokud uživatel nesouhlasil
        // (user.termsAcceptedAt == null), pustíme ho výhradně na stránku s podmínkami
        // (meta.termsPage) a přeskočíme kontrolu oprávnění.
        const { hasPlugins } = usePlugins()
        if (hasPlugins('admin.terms') && auth.value.user) {
          const termsPage = router.getRoutes().find((r) => r.meta.termsPage)?.path
          if (!auth.value.user.termsAcceptedAt) {
            if (termsPage && to.path !== termsPage) {
              return termsPage
            }
            return
          }
          // Už souhlasil → na stránce s podmínkami nemá co dělat.
          if (termsPage && to.path === termsPage) {
            return getEntryPage()
          }
        }

        const requiredPermissions = (
          typeof to.meta.permissions === 'function'
            ? to.meta.permissions(to)
            : (to.meta.permissions ?? [])
        ) as string[]

        for (let i = 0; i < requiredPermissions.length; i++) {
          const p = requiredPermissions[i]
          if (!p || !permissions.value[p]) {
            await throwMissingPermissionsError(p)
          }
        }
        if (to.meta.logoutPage) {
          logout()
        }
      },
      { global: true }
    )

    addRouteMiddleware('guest', async (_, to) => {
      if (!sanctumSpa && auth.value.token === null) {
        getToken()
      }

      if (!sessionVerified) {
        try {
          await checkSession()

          sessionVerified = true
        } catch {
          // Not logged in – allow access to guest pages
          return
        }
      }

      if (!to.meta.loginPage && isLoggedIn() && permissions.value) {
        const toast = usePrimeVueToast()
        toast.add({
          severity: 'warn',
          summary: t('Nedostatečná oprávnění'),
          life: 600000,
        })
      }
    })
    if (nuxtApp.$sharedComponentsPlugins?.eshop) {
      const { loadBasket } = useBasketService()
      loadBasket()
    }
    const auth = useState<Auth>('auth', () => {
      return {
        user: null,
        token: null,
        permissions: null,
        foreignUser: isForeignUserCookie?.value === 'true' || false,
        expiresAt: expiresAtCookie.value ? new Date(parseInt(expiresAtCookie.value)) : null,
      }
    })

    const getToken = () => {
      auth.value.token = useCookie(tokenCookieKey, { sameSite: true, secure: true })?.value || null
    }

    const tokenCookie = useCookie(tokenCookieKey, { sameSite: true, secure: true })

    const getUser = async () => {
      if (auth.value.token) {
        try {
          const { data } = await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions`, {
            method: 'GET',
            headers: { Accept: 'application/json', Authorization: `Bearer ${auth.value.token}` },
          })
          if (!data?.identity) {
            auth.value.token = undefined
            tokenCookie.value = null
            return
          }
          auth.value.permissions = data.abilities
          auth.value.user = data.identity
        } catch {
          // Selhání /sessions (síť/CORS/401/500) nesmí shodit inicializaci pluginu.
          // Necháme uživatele jako nepřihlášeného – middleware ho pošle na login.
        }
      }
    }

    if (!sanctumSpa) {
      if (auth.value.token === null) {
        getToken()
      }
      if (auth.value.user === null) {
        await getUser()
      }
    } else if (auth.value.user === null) {
      // SPA: nemáme token, session načteme z httponly cookie (SSR i klient)
      await checkSession()
    }

    const setToken = (token: string, expires?: Date) => {
      useCookie(tokenCookieKey, { sameSite: true, secure: true, expires }).value = token
      if (expires) {
        expiresAtCookie.value = new Date(expires).getTime().toString()
        auth.value.expiresAt = new Date(expires)
      } else {
        auth.value.expiresAt = null
      }
    }

    const clearToken = () => {
      useCookie(tokenCookieKey, { sameSite: true, secure: true }).value = null
    }

    const loginAs = async (uuid: string) => {
      const { addErrorMessage } = useToastService()
      try {
        const headers: { [key: string]: string } = { Accept: 'application/json' }
        if (auth.value.token) {
          headers['Authorization'] = `Bearer ${auth.value.token}`
        }
        const { data } = await $fetch<{ data: SessionData }>(
          `${apiBaseUrl}/sessions/incarnations`,
          {
            method: 'POST',
            headers,
            body: {
              loginAs: uuid,
              tokenName: 'incarnation',
              device: {
                os,
                browser,
                type: 'eshop-client',
              },
            },
          }
        )
        await afterLoginLogic(data, true)
      } catch (e) {
        if (e?.response?._data?.errors?.[0]?.message) {
          addErrorMessage(e?.response?._data?.errors?.[0]?.message)
        }
        throw e
      }
    }

    const refetchBasket = (forceDelete: boolean = false) => {
      const { clear } = useBasketService()
      nuxtApp.runWithContext(() => {
        const queryClient = useQueryClient()
        queryClient.removeQueries({
          queryKey: ['basket'],
        })
        if (forceDelete) {
          clear()
        }
      })
    }

    const login = async (
      input:
        | {
            email: string
            password: string
          }
        | { card: string },
      config: LoginConfig = {}
    ) => {
      try {
        const { $sharedComponentsOptions } = useNuxtApp()
        const identifier = $sharedComponentsOptions?.identifier
        const optionsType = $sharedComponentsOptions?.type
        const type = identifier || optionsType ? `${identifier ?? optionsType}-client` : 'client'
        const tokenName = type
        const headers: { [key: string]: string } = {
          Accept: 'application/json',
          ...config.headers,
        }
        let credentials: 'include' | undefined = undefined
        let loginUrl = `${apiBaseUrl}/sessions`
        let body: Record<string, unknown> = {}
        if ('card' in input) {
          body = {
            card: input.card,
            tokenName: 'card',
            device: {
              os,
              browser,
              type,
            },
          }
          loginUrl = `${apiBaseUrl}/sessions/cards`
        } else {
          body = {
            username: input?.email,
            password: input?.password,
            tokenName,
            device: {
              os,
              browser,
              type,
            },
          }
        }
        if (sanctumSpa && !('card' in input)) {
          const url = new URL(apiBaseUrl)
          // Step 1: Obtain the XSRF-TOKEN cookie from Laravel Sanctum.
          // `credentials: 'include'` is required – the API runs on a different origin,
          // and without it the browser discards the Set-Cookie response headers.
          await $fetch(`${url.origin}/sanctum/csrf-cookie`, { credentials: 'include' })
          // Step 2: Read the XSRF-TOKEN value – it is not httponly so JS can read it
          const xsrfMatch = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
          const xsrfToken = xsrfMatch ? decodeURIComponent(xsrfMatch[1]!) : ''
          // Step 3: Authenticate via SPA endpoint (Laravel sets the httponly session cookie)
          headers['X-XSRF-TOKEN'] = xsrfToken
          loginUrl = `${apiBaseUrl}/sessions/spa`
          credentials = 'include'
          body = {
            username: input.email,
            password: input.password,
            device: {
              os,
              browser,
              type,
            },
          }
        }

        if (config.body) {
          if (typeof config.body === 'function') {
            body = config.body(body)
          } else {
            body = config.body
          }
        }
        if (config.loginUrl) {
          if (typeof config.loginUrl === 'function') {
            loginUrl = config.loginUrl(apiBaseUrl)
          } else {
            loginUrl = config.loginUrl
          }
        }
        const { data } = await $fetch<{ data: SessionData & Partial<LoginChallenge> }>(loginUrl, {
          method: 'POST',
          headers,
          body,
          credentials,
        })

        // 202 = vyžadováno OTP. ofetch bere 202 jako úspěch, takže větvíme podle
        // obsahu odpovědi (špatné heslo = 422 → skončí v catch volajícího).
        if (data?.challengeId) {
          return {
            challenge: {
              challengeId: data.challengeId,
              challengeMethod: data.challengeMethod,
            } as LoginChallenge,
          }
        }

        await afterLoginLogic(data)
        return { challenge: null }
      } catch (e) {
        throw e
      }
    }

    // Dokončení dvoufaktorového ověření – odešle OTP kód k challenge z `login`.
    const completeChallenge = async (challengeId: string, code: string) => {
      const headers: Record<string, string> = { Accept: 'application/json' }
      let credentials: 'include' | undefined = undefined
      if (sanctumSpa) {
        const url = new URL(apiBaseUrl)
        // `credentials: 'include'` – cross-origin API, otherwise Set-Cookie is dropped
        await $fetch(`${url.origin}/sanctum/csrf-cookie`, { credentials: 'include' })
        const xsrfMatch = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
        headers['X-XSRF-TOKEN'] = xsrfMatch ? decodeURIComponent(xsrfMatch[1]!) : ''
        credentials = 'include'
      }
      const { data } = await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions/spa/challenge`, {
        method: 'POST',
        headers,
        credentials,
        body: { challengeId, code },
      })
      await afterLoginLogic(data)
    }

    const afterLoginLogic = async (data: SessionData, foreignUser = false) => {
      if (data.token) {
        setToken(data.token, data.expiresAt ? new Date(data.expiresAt.iso) : undefined)
        auth.value.token = data.token
      }
      if (data.identity) {
        auth.value.user = data.identity
      }
      if (data.abilities) {
        auth.value.permissions = data.abilities
      }
      auth.value.foreignUser = foreignUser
      isForeignUserCookie.value = foreignUser ? 'true' : 'false'

      if (nuxtApp.$sharedComponentsPlugins?.eshop) {
        const { basket } = useBasketService()
        if (basket.value.guid) {
          refetchBasket()
        } else {
          // Pokud nemám košík načtu košíky uživatele a dám je do state
          const userBasketsResponse = await useCustomFetch<{ data: object[] }>({
            url: '/self/baskets',
            query: {
              open: 1,
            },
            clientOnly: true,
          })
          const userBaskets = useState('user-baskets', () => [])
          userBaskets.value = userBasketsResponse.data ?? []
        }
        const { userLogin: dataLayerUserLogin } = useDataLayers()
        dataLayerUserLogin(auth.value.user!)
      }

      if (
        $sentry &&
        typeof $sentry === 'object' &&
        'setUser' in $sentry &&
        typeof $sentry.setUser === 'function'
      ) {
        $sentry.setUser({
          username: data.identity.username,
          email: data.identity.email,
          id: data.identity.id,
        })
      }

      if (data.identity && nuxtApp.$sharedComponentsPlugins?.firebase) {
        // Zajstí, že se vygeneroval vždy nový a né starý FCM token
        const registerFCM = async () => {
          const { getToken: getFcmToken, deleteToken } = useFcm()
          const token = await getFcmToken()
          if (token) {
            const printerHash = window.printerHash
            await deleteToken(token, 'all')
            const newPushToken = await getFcmToken()
            // Kontrola, zda se uživatel již neodhlásil
            if (auth.value.user) {
              if (newPushToken) {
                const promises = [registerPushToken(newPushToken, 'profile')]
                if (printerHash) {
                  promises.push(registerPushToken(newPushToken, 'device', printerHash))
                }
                await Promise.all(promises)
              }
            }
          }
        }
        registerFCM()
      }

      const localeBackLink = getBackLink()

      const backLink = localeBackLink ? localeBackLink : getEntryPage()

      removeBackLink()
      if (backLink) {
        appNavigateTo(backLink)
      } else {
        const toast = usePrimeVueToast()
        toast.add({
          severity: 'warn',
          summary: t('Nedostatečná oprávnění'),
          detail: t('Nemáte oprávnění k žádné části aplikace.'),
          life: 600000,
        })
        showError(
          createError({
            statusMessage: t('Nedostatečná oprávnění'),
            statusText: t('Nemáte oprávnění k žádné části aplikace.'),
            statusCode: 403,
            fatal: true,
          })
        )
      }
    }

    const logout = async (logoutOnBackend: boolean = true) => {
      try {
        if (nuxtApp.$sharedComponentsPlugins?.firebase) {
          const { fcmToken, deleteToken } = useFcm()
          if (fcmToken.value) {
            try {
              await deleteToken(fcmToken.value, 'profile')
            } catch {}
          }
        }
        if (logoutOnBackend) {
          try {
            const headers: Record<string, string> = { Accept: 'application/json' }
            if (auth.value.token) {
              headers['Authorization'] = `Bearer ${auth.value.token}`
            } else if (sanctumSpa) {
              const xsrfMatch = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/)
              const xsrfToken = xsrfMatch ? decodeURIComponent(xsrfMatch[1]!) : ''
              if (xsrfToken) headers['X-XSRF-TOKEN'] = xsrfToken
            }

            await $fetch<{ data: SessionData }>(`${apiBaseUrl}/sessions`, {
              method: 'DELETE',
              headers,
              // SPA: odeslat httponly cookie, aby backend zneplatnil session
              credentials: sanctumSpa ? 'include' : undefined,
            })
          } catch (e) {
            console.error(e)
          }
        }
        // Vše níže používá Nuxt composables (useCookie, useLocalePath, useRouter, …).
        // Po předchozích awaitech už nejsme v Nuxt kontextu (typicky při SSR, kdy
        // logout spustí 401 z API), proto celý zbytek běží přes runWithContext.
        await nuxtApp.runWithContext(async () => {
          clearToken()
          if (import.meta.client) {
            tokenCookie.value = null
          }
          auth.value = {
            permissions: null,
            token: null,
            user: null,
            expiresAt: null,
          }
          if (
            $sentry &&
            typeof $sentry === 'object' &&
            'setUser' in $sentry &&
            typeof $sentry.setUser === 'function'
          ) {
            $sentry.setUser(null)
          }
          const localePath = useLocalePath()
          if (nuxtApp.$sharedComponentsPlugins?.eshop) {
            refetchBasket(true)
          }
          const queryClient = useQueryClient()
          queryClient.invalidateQueries({ refetchType: 'none' })
          addSuccessMessage(t('Odhlášení proběhlo úspěšně'))
          const router = useRouter()
          const loginPage = router.getRoutes().find((r) => r.meta.loginPage)?.path
          appNavigateTo(loginPage ?? localePath({ name: 'login' }))
        })
      } catch (e) {
        addErrorMessage(t('Při odhlášení došlo k chybě'))
        console.error(e)
      }
    }

    const isRoot = computed(() => {
      return auth.value?.permissions && auth.value?.permissions?.includes('root')
    })

    const permissions = computed(() => {
      const permissionsObject: { [key: string]: boolean } = {}
      ;(auth.value?.permissions ?? []).forEach((p: string) => {
        permissionsObject[p] = true
      })
      return permissionsObject
    })

    nuxtApp.vueApp.directive('can', {
      beforeMount(el, binding) {
        const comment = document.createComment('v-can')
        el.__vueCanPlaceholder__ = comment
        if (!(binding.value ?? []).some((p: string) => permissions.value[p])) {
          if (el.parentNode) {
            el.parentNode.replaceChild(comment, el)
          }
        }
      },
      updated(el, binding) {
        const comment = el.__vueCanPlaceholder__

        if ((binding.value ?? []).some((p: string) => permissions.value[p])) {
          if (comment.parentNode) {
            comment.parentNode.replaceChild(el, comment)
          }
        } else {
          if (el.parentNode) {
            el.parentNode.replaceChild(comment, el)
          }
        }
      },
    })

    const getBackLink = () => {
      const link = localStorage.getItem(backLinkKey)
      if (link) {
        try {
          let serializedValue
          try {
            serializedValue = JSON.parse(link)
          } catch {
            serializedValue = link
          }
          return serializedValue
        } catch (e) {
          console.error(e)
        }
      }
      return null
    }

    const setBackLink = (value: string) => {
      localStorage.setItem(backLinkKey, value)
    }

    const removeBackLink = () => {
      localStorage.removeItem(backLinkKey)
    }

    return {
      provide: {
        auth: {
          auth,
          login,
          completeChallenge,
          loginAs,
          logout,
          afterLoginLogic,
          isRoot,
          permissions,
          checkSession,
          getEntryPage,
          getBackLink,
          setBackLink,
          removeBackLink,
        },
      },
    }
  },
})
